import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import { Button } from 'tio-ui/components/buttons';
import { eq } from 'tio-ui/utilities';
import SectionHeader from 'tio-reimagined/components/section-header';
import SmartActionCard from 'tio-employee/components/reimagined/smart-action-card';
import type { SmartAction } from 'tio-employee/components/reimagined/smart-action-card';
import NavCard from 'tio-reimagined/components/nav-card';
import PageHero from 'tio-reimagined/components/page-hero';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import type { DataSourceInfo } from 'tio-employee/services/reimagined-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import {
  PhPiggyBank,
  PhArrowRight,
  PhTrendUp,
  PhChartLineUp,
  PhHandshake,
  PhShieldCheck,
  PhTarget,
  PhLightbulb,
  PhBank,
  PhSparkle,
  PhRocket,
  PhCalculator,
  PhScales,
  PhRobot,
} from 'ember-phosphor-icons';

interface SyfIndexModel {
  dataSourceInfo: DataSourceInfo;
  employerContributions: {
    contributions: {
      yearToDate: number;
      lifetime: number;
      projectedAnnual: number;
      monthlyAmount: number;
      annualLimit: number;
    };
    isLiveData: boolean;
  };
}

interface SyfIndexSignature {
  Args: {
    model: SyfIndexModel;
  };
}

interface SyfInsight {
  title: string;
  value: string;
  description: string;
  intent: 'success' | 'warning' | 'info';
}

export default class SyfIndex extends Component<SyfIndexSignature> {
  @service declare reimaginedData: ReimaginedDataService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get contributionsSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.args.model.employerContributions.isLiveData ? 'live' : 'placeholder';
  }

  get smartActions(): SmartAction[] {
    return [
      {
        id: 'enrollment',
        title: 'Enroll in 401(k)',
        description: 'Start building your retirement savings with automatic payroll deductions.',
        icon: PhPiggyBank,
        priority: 'urgent',
        category: 'money',
        cta: 'Enroll Now',
        route: 'authenticated.reimagined.advisor',
      },
      {
        id: 'maximize-match',
        title: 'Maximize Employer Match',
        description:
          "Don't leave free money on the table. Contribute enough to get the full match.",
        icon: PhHandshake,
        priority: 'high',
        category: 'opportunity',
        cta: 'Optimize Match',
        route: 'authenticated.reimagined.advisor',
      },
      {
        id: 'review-investments',
        title: 'Review Investments',
        description: 'Make sure your allocation matches your risk tolerance and timeline.',
        icon: PhChartLineUp,
        priority: 'medium',
        category: 'money',
        cta: 'Review Now',
        route: 'authenticated.reimagined.advisor',
      },
    ];
  }

  get insights(): SyfInsight[] {
    return [
      {
        title: 'Employer Match',
        value: '100% up to 6%',
        description: 'Your employer matches dollar-for-dollar on the first 6% of your salary',
        intent: 'success',
      },
      {
        title: 'Vesting Schedule',
        value: '3 years',
        description: "You're 67% vested. Full vesting at 3 years of service.",
        intent: 'info',
      },
      {
        title: 'Tax Savings',
        value: '$2,400/yr',
        description: 'Estimated annual tax savings from pre-tax contributions',
        intent: 'success',
      },
      {
        title: 'Retirement Readiness',
        value: '62%',
        description: 'On track for 62% of your retirement income goal',
        intent: 'warning',
      },
    ];
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="gap-8 pb-12">
      {{! Hero Section }}
      <PageHero @theme="emerald" @decoration="circles" @rounded="2xl">
        <:default>
          <div class="flex items-center gap-3 mb-4">
            <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20">
              <PhPiggyBank class="h-7 w-7 text-white" />
            </div>
            <div>
              <Heading @level={{2}} class="text-white text-2xl font-bold">
                Secure Your Future
              </Heading>
              <Text class="text-emerald-100 text-sm">
                Retirement savings & 401(k) management
              </Text>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
            <div class="bg-white/10 rounded-xl p-4">
              <Text class="text-emerald-200 text-xs uppercase tracking-wide">Account Balance</Text>
              <div class="text-2xl font-bold text-white mt-1">$47,850</div>
              <div class="flex items-center gap-1 mt-1">
                <PhTrendUp class="h-3 w-3 text-emerald-300" />
                <Text class="text-emerald-300 text-xs">+12.3% YTD</Text>
              </div>
            </div>
            <div class="bg-white/10 rounded-xl p-4">
              <Text class="text-emerald-200 text-xs uppercase tracking-wide">Your Contribution</Text>
              <div class="text-2xl font-bold text-white mt-1">8%</div>
              <Text class="text-emerald-200 text-xs">$4,800/year</Text>
            </div>
            <div class="bg-white/10 rounded-xl p-4">
              <Text class="text-emerald-200 text-xs uppercase tracking-wide">Employer Match</Text>
              <div class="text-2xl font-bold text-emerald-300 mt-1">$3,600</div>
              <Text class="text-emerald-200 text-xs">Free money earned</Text>
            </div>
            <div class="bg-white/10 rounded-xl p-4">
              <Text class="text-emerald-200 text-xs uppercase tracking-wide">Projected at 65</Text>
              <div class="text-2xl font-bold text-white mt-1">$1.2M</div>
              <Text class="text-emerald-200 text-xs">At current rate</Text>
            </div>
          </div>
        </:default>
      </PageHero>

      {{! Smart Actions }}
      <SectionHeader
        @icon={{PhLightbulb}}
        @title="Next Steps"
        @subtitle="Actions to secure your financial future"
        @iconClass="text-amber-500"
      />
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        {{#each this.smartActions as |action|}}
          <SmartActionCard @item={{action}} />
        {{/each}}
      </div>

      {{! Insights Grid }}
      <SectionHeader
        @icon={{PhChartLineUp}}
        @title="Your 401(k) Insights"
        @subtitle="Key metrics at a glance"
        @iconClass="text-emerald-500"
      />
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {{#each this.insights as |insight|}}
          <div
            class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5"
          >
            <Text class="text-gray-500 text-xs uppercase tracking-wide">{{insight.title}}</Text>
            <div
              class="text-xl font-bold mt-1
                {{if
                  (eq insight.intent 'success')
                  'text-emerald-600'
                  (if (eq insight.intent 'warning') 'text-amber-600' 'text-blue-600')
                }}"
            >
              {{insight.value}}
            </div>
            <Text class="text-gray-500 dark:text-gray-400 text-xs mt-2">
              {{insight.description}}
            </Text>
          </div>
        {{/each}}
      </div>

      {{! Optimization Tips }}
      <SectionHeader
        @icon={{PhSparkle}}
        @title="Optimization Tips"
        @subtitle="Ways to boost your savings"
        @iconClass="text-purple-500"
      />
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6"
        >
          <div class="flex items-center gap-3 mb-4">
            <div
              class="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-900/30"
            >
              <PhRocket class="h-5 w-5 text-emerald-600" />
            </div>
            <Heading @level={{4}} class="font-semibold">Increase by 1%</Heading>
          </div>
          <Text class="text-gray-600 dark:text-gray-400 text-sm">
            Increasing your contribution by just 1% could add $150,000+ to your retirement. Most
            people don't even notice the difference in their paycheck.
          </Text>
        </div>

        <div
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6"
        >
          <div class="flex items-center gap-3 mb-4">
            <div
              class="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30"
            >
              <PhShieldCheck class="h-5 w-5 text-blue-600" />
            </div>
            <Heading @level={{4}} class="font-semibold">Roth vs Traditional</Heading>
          </div>
          <Text class="text-gray-600 dark:text-gray-400 text-sm">
            Consider a Roth 401(k) if you expect to be in a higher tax bracket in retirement. You
            pay taxes now but withdrawals are tax-free.
          </Text>
        </div>

        <div
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6"
        >
          <div class="flex items-center gap-3 mb-4">
            <div
              class="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30"
            >
              <PhTarget class="h-5 w-5 text-purple-600" />
            </div>
            <Heading @level={{4}} class="font-semibold">Target-Date Funds</Heading>
          </div>
          <Text class="text-gray-600 dark:text-gray-400 text-sm">
            Set it and forget it. Target-date funds automatically adjust your portfolio mix as you
            approach retirement.
          </Text>
        </div>

        <div
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6"
        >
          <div class="flex items-center gap-3 mb-4">
            <div
              class="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/30"
            >
              <PhSparkle class="h-5 w-5 text-amber-600" />
            </div>
            <Heading @level={{4}} class="font-semibold">Loan vs Hardship</Heading>
          </div>
          <Text class="text-gray-600 dark:text-gray-400 text-sm">
            Need emergency funds? A 401(k) loan is usually better than a hardship withdrawal. You
            repay yourself with interest.
          </Text>
        </div>
      </div>

      {{! Tools }}
      <SectionHeader
        @icon={{PhBank}}
        @title="Retirement Tools"
        @subtitle="Plan smarter"
        @iconClass="text-blue-500"
      />
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <NavCard
          @layout="gradient"
          @title="Retirement Calculator"
          @subtitle="Project your retirement savings based on contributions and growth"
          @icon={{PhCalculator}}
          @route="authenticated.reimagined.finance"
        />
        <NavCard
          @layout="gradient"
          @title="Section 127 Optimizer"
          @subtitle="Balance student loans and retirement savings for maximum benefit"
          @icon={{PhScales}}
          @route="authenticated.reimagined.finance.optimizer"
        />
        <NavCard
          @layout="gradient"
          @title="Financial Advisor"
          @subtitle="Get AI-powered guidance on your overall financial strategy"
          @icon={{PhRobot}}
          @route="authenticated.reimagined.advisor"
        />
      </div>

      {{! CTA }}
      <div
        class="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-2xl p-8 text-center"
      >
        <Heading @level={{3}} class="text-xl font-bold mb-2">
          Balance Student Loans & Retirement
        </Heading>
        <Text class="text-gray-600 dark:text-gray-400 mb-6">
          Our Section 127 Optimizer helps you allocate your employer benefit between loan repayment
          and retirement savings for maximum impact.
        </Text>
        <LinkTo @route="authenticated.reimagined.finance.optimizer">
          <Button @intent="primary">
            Optimize My Benefits
            <PhArrowRight class="ml-2 h-4 w-4" />
          </Button>
        </LinkTo>
      </div>
    </VStack>
  </template>
}
